import type { ButtonHTMLAttributes, ReactNode } from 'react'
import type { VariantProps } from 'class-variance-authority'

import { cva } from 'class-variance-authority'
import { cn } from '@/lib/utils'

export const buttonVariants = cva(
    'inline-flex shrink-0 items-center justify-center gap-2 rounded-full font-semibold whitespace-nowrap transition-[color,background-color,border-color,box-shadow,transform] duration-200 outline-none select-none focus-visible:ring-4 focus-visible:ring-ring/30 disabled:pointer-events-none disabled:opacity-50 active:translate-y-px [&_svg]:pointer-events-none [&_svg]:shrink-0',
    {
        variants: {
            variant: {
                default: 'bg-primary text-primary-foreground shadow-lg shadow-brand-700/20 hover:bg-brand-700',
                outline: 'border border-border bg-white text-ink hover:border-brand-200 hover:bg-brand-50',
                soft: 'bg-brand-50 text-brand-700 hover:bg-brand-100',
                ghost: 'text-ink hover:bg-brand-50',
                inverse: 'bg-white text-ink hover:bg-brand-50',
                danger: 'bg-rose-600 text-white shadow-lg shadow-rose-700/20 hover:bg-rose-700'
            },
            size: {
                sm: 'h-9 px-4 text-sm',
                default: 'h-11 px-5 text-sm',
                lg: 'h-12 px-6 text-[15px]',
                icon: 'size-10'
            }
        },
        defaultVariants: {
            variant: 'default',
            size: 'default'
        }
    }
)

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {}

export const Button = ({
    className,
    variant,
    size,
    type = 'button',
    ...props
}: ButtonProps): ReactNode => {
    return (
        <button
            type={type}
            data-slot='button'
            className={cn(buttonVariants({ variant, size }), className)}
            {...props}
        />
    )
}
